/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { effortLabelsFor } from '@linc-technologies/linced-common/utils/effort-labels';
import { getCurrentMilestone } from '@linc-technologies/linced-common/utils/progressions';
import { tagName } from '@ember-decorators/component';
import { byKeys, findBy } from '@linc-technologies/linced-common/utils/arrays';
import {
  resolveRubric,
  rubricElements,
} from '@linc-technologies/linced-common/utils/rubrics';
import { generateRubricComments } from 'linced-app/utils/rubric-comments';
import {
  latestProgressionMap,
  progressionSource,
} from 'linced-app/utils/progression-history';

@tagName('')
export default class ProgressPanel extends Component {
  // Services
  @service
  dates;

  @service
  store;

  // passed in
  progressComponents = null;

  subject = null;
  @tracked student = null;
  // The report template, for the name format the generated wording uses.
  reportTemplate = null;
  section = null;
  // The rubric family chosen on the report template section (null for the
  // default family)
  rubricFamilyId = null;
  onJudgementChange = null;
  onEffortChange = null;
  onViewRubric = null;
  onAutoPopulate = null;
  progressionsPreloaded = false;

  // True when all judgement data has been recorded for this student's section.
  // The student-report-section record is the source of truth; historical OTJ
  // progressions are not considered for completion (they may pre-fill slider
  // positions for UX, but a draft is only "complete" once the teacher has
  // saved a value onto the section itself).
  @computed(
    'section.judgement',
    'section.elementProgressions.@each.{elementId,judgement}',
    'rubricElements.[]',
    'hasStrandVisual',
  )
  get judgementIsComplete() {
    // Overall judgement is required — must be set on the section record.
    let overallJudgement = this.get('section.judgement');
    if (!overallJudgement) return false;
    // Per-element judgements are only required when strand-visual is
    // present (the only UI that exposes per-element entry). With only
    // progress-descriptor the overall slider alone is sufficient.
    let elements = this.rubricElements || [];
    if (!this.hasStrandVisual || !elements.length) return true;
    // Every rubric element must also have a judgement recorded on the section.
    let sectionElements = this.get('section.elementProgressions') || [];
    return elements.every((el) => {
      let entry = findBy(sectionElements, 'elementId', el.get('id'));
      return !!(entry && entry.get('judgement'));
    });
  }

  // True when all effort data has been recorded for this student's section.
  // Source of truth is the section record; historical OTJ progressions are
  // not considered for completion. Per-element effort is only required when
  // effortPerElement (strand-visual with table-effort visualType) — other
  // report types only track a single overall effort slider.
  @computed(
    'section.effort',
    'section.elementProgressions.@each.{elementId,effort}',
    'rubricElements.[]',
    'effortPerElement',
  )
  get effortIsComplete() {
    // Overall effort is required — must be set on the section record.
    let overallEffort = this.get('section.effort');
    if (!overallEffort) return false;
    // This report type only has one overall effort slider — no per-element check needed.
    if (!this.effortPerElement) return true;
    // No rubric elements — overall slider alone is sufficient.
    let elements = this.rubricElements || [];
    if (!elements.length) return true;
    // Every rubric element must also have effort recorded on the section.
    let sectionElements = this.get('section.elementProgressions') || [];
    return elements.every((el) => {
      let entry = findBy(sectionElements, 'elementId', el.get('id'));
      return !!(entry && entry.get('effort'));
    });
  }

  @computed('progressComponents.[]')
  get hasStrandVisual() {
    return (this.progressComponents || []).some(function (component) {
      return component.type === 'strand-visual';
    });
  }

  @computed('progressComponents.[]')
  get hasProgressDescriptor() {
    return (this.progressComponents || []).some(function (component) {
      return component.type === 'progress-descriptor';
    });
  }

  @computed('progressComponents.[]')
  get hasEffort() {
    return (this.progressComponents || []).some(function (component) {
      return (
        component.type === 'effort' ||
        (component.type === 'strand-visual' &&
          component.visualType === 'table-effort')
      );
    });
  }

  // effort per element when strand-visual visualType is 'table-effort'
  @computed('progressComponents.[]')
  get effortPerElement() {
    let strandVisual = (this.progressComponents || []).find(
      function (component) {
        return component.type === 'strand-visual';
      },
    );
    return !!(strandVisual && strandVisual.visualType === 'table-effort');
  }

  @computed('subject.possibleGrades.[]')
  get judgementLevels() {
    return this.get('subject.possibleGrades') || [];
  }

  @computed('subject.{possibleGrades.[],school.jurisdiction}')
  get effortLevels() {
    let possibleGrades = this.get('subject.possibleGrades') || [];
    let jurisdiction = this.get('subject.school.jurisdiction');
    return effortLabelsFor(jurisdiction).map(function (entry) {
      let grade = possibleGrades.find((g) => g.value === entry.value);
      return {
        value: entry.value,
        string: entry.label,
        colour: grade ? grade.colour || '#26a69a' : '#26a69a',
      };
    });
  }

  @computed('subject.templateId', 'student', 'rubricFamilyId')
  get rubric() {
    return resolveRubric(
      this.subject,
      this.student,
      this.store,
      this.dates.rolloverMonthDate,
      this.rubricFamilyId,
    );
  }

  @computed('rubric', 'student')
  get rubricElements() {
    return rubricElements(this.rubric, this.store);
  }

  @computed('section.judgement', 'judgementProgressions')
  get overallJudgementValue() {
    let judgement = this.get('section.judgement');
    if (judgement) {
      return parseInt(judgement, 10);
    }
    let prog =
      this.judgementProgressions && this.judgementProgressions['overall'];
    // 0 (unspecified, the Go zero value) is no judgement.
    return (prog && prog.get('judgement')) || null;
  }

  // True when the overall judgement slider is showing a value pre-filled
  // from a previous OTJ progression rather than one saved on this report.
  @computed('section.judgement', 'judgementProgressions')
  get overallJudgementIsPrevious() {
    if (this.get('section.judgement')) return false;
    let prog =
      this.judgementProgressions && this.judgementProgressions['overall'];
    return !!(prog && prog.get('judgement') > 0);
  }

  @computed('section.effort', 'effortProgressions')
  get overallEffortValue() {
    let effort = this.get('section.effort');
    if (effort) {
      return parseInt(effort, 10);
    }
    let prog = this.effortProgressions && this.effortProgressions['overall'];
    // 0 (the Go zero value) is no effort.
    return (prog && prog.get('effort')) || null;
  }

  @computed('section.effort', 'effortProgressions')
  get overallEffortIsPrevious() {
    if (this.get('section.effort')) return false;
    let prog = this.effortProgressions && this.effortProgressions['overall'];
    return !!(prog && prog.get('effort') > 0);
  }

  // Where the pre-filled overall judgement / effort came from, for the
  // slider's "Previously entered" badge.
  @computed('judgementProgressions')
  get overallJudgementSource() {
    return progressionSource(this.judgementProgressions?.overall, this.store);
  }

  @computed('effortProgressions')
  get overallEffortSource() {
    return progressionSource(this.effortProgressions?.overall, this.store);
  }

  @computed('subject.milestones.[]', 'student')
  get currentMilestone() {
    let milestones = this.get('subject.milestones') || [];
    return getCurrentMilestone(
      milestones,
      this.student,
      this.dates.rolloverMonthDate,
    );
  }

  // Each strand's and the overall judgement's / effort's progressions from
  // every milestone and year. Passed in by the write pages (loaded once for
  // the whole group); loaded here otherwise.
  @tracked judgementHistory = null;
  @tracked effortHistory = null;

  // Bumped when earlier rubrics' strands finish loading, so the maps below
  // can match them by name.
  @tracked _earlierElementsLoaded = 0;

  // The most recent judgement / effort per strand (keyed by element id) and
  // 'overall', shown greyed out until the teacher sets this report's value.
  // Earlier rubrics' strands are matched to this rubric's by name.
  @computed(
    'judgementHistory.[]',
    'rubricElements.[]',
    '_earlierElementsLoaded',
  )
  get judgementProgressions() {
    return latestProgressionMap(
      this.judgementHistory,
      this.rubricElements,
      this.store,
    );
  }

  @computed('effortHistory.[]', 'rubricElements.[]', '_earlierElementsLoaded')
  get effortProgressions() {
    return latestProgressionMap(
      this.effortHistory,
      this.rubricElements,
      this.store,
    );
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (this.progressionsPreloaded) {
      this._loadEarlierElements();
      return;
    }
    this._loadElementProgressions();
  }

  // History can point at strands of an earlier rubric that is no longer
  // active, which the reporting route doesn't load. Fetch those strands (once
  // each) so they can be matched to this rubric's strands by name.
  _loadEarlierElements() {
    let currentIds = new Set(
      (this.rubricElements || []).map((element) => element.get('id')),
    );
    let requested = (this._requestedElementIds ??= new Set());
    let missing = new Set();
    for (let history of [this.judgementHistory, this.effortHistory]) {
      for (let progression of history || []) {
        let elementId = progression.get('elementId');
        if (
          elementId &&
          !currentIds.has(elementId) &&
          !requested.has(elementId) &&
          !this.store.peekRecord('reports/rubric-element', elementId)
        ) {
          missing.add(elementId);
        }
      }
    }
    if (!missing.size) return;

    for (let id of missing) requested.add(id);
    Promise.all(
      Array.from(missing, (id) =>
        this.store.findRecord('reports/rubric-element', id).catch(() => null),
      ),
    ).then(() => {
      if (this.isDestroying || this.isDestroyed) return;
      this._earlierElementsLoaded++;
    });
  }

  _loadElementProgressions() {
    let student = this.student;
    let subject = this.subject;
    if (!student || !subject) {
      this.judgementHistory = [];
      this.effortHistory = [];
      return;
    }

    // The panel stays open while group review moves to the next student, so
    // drop a response that arrives for a student no longer shown.
    let studentId = student.get('id');
    let query = {
      personId: studentId,
      goalSubjectId: subject.get('id'),
      elements: true,
    };
    let load = (key, extra) =>
      this.store
        .query('milestones/progression', { ...query, ...extra })
        .then((progressions) => {
          if (this.isDestroying || this.isDestroyed) return;
          if (this.student?.id !== studentId) return;
          this[key] = [...progressions];
          this._loadEarlierElements();
        })
        .catch(() => {
          if (this.isDestroying || this.isDestroyed) return;
          if (this.student?.id !== studentId) return;
          // Fall back to no history so completeness checks rely on the
          // section record rather than waiting forever.
          this[key] = [];
        });

    // Judgement progressions (no effort flag), then effort progressions
    load('judgementHistory', {});
    load('effortHistory', { effort: true });
  }

  @computed(
    'rubricElements.[]',
    'judgementLevels.[]',
    'judgementProgressions',
    'effortProgressions',
    'section.elementProgressions.@each.{elementId,judgement,effort}',
  )
  get rubricElementsWithTooltips() {
    let elements = this.rubricElements;
    let levels = this.judgementLevels;
    let allCriteria = this.store.peekAll('reports/rubric-criteria');
    let judgementMap = this.judgementProgressions || {};
    let effortMap = this.effortProgressions || {};
    let store = this.store;
    let sectionElements = this.get('section.elementProgressions') || [];

    return elements.map(function (element) {
      let elementId = element.get('id');
      let elementCriteria = allCriteria.filter(function (criteria) {
        return criteria.belongsTo('elementId').id() === elementId;
      });

      let tooltips = levels.map(function (level) {
        let items = elementCriteria
          .filter(function (criteria) {
            return criteria.get('progressColumn') === level.value;
          })
          .sort(byKeys('sequence'))
          .map(function (criteria) {
            return criteria.get('text');
          });
        return { label: level.string, items };
      });

      // Check section record first, fall back to progressions
      let sectionEntry = findBy(sectionElements, 'elementId', elementId);
      let judgementValue = null;
      let effortValue = null;
      let isNA = false;
      let isPrevious = false;
      let isEffortPrevious = false;
      let judgementSource = '';
      let effortSource = '';

      if (sectionEntry && sectionEntry.get('judgement')) {
        let raw = sectionEntry.get('judgement');
        if (raw === 'na') {
          isNA = true;
        } else {
          judgementValue = parseInt(raw, 10);
        }
      } else {
        let prog = judgementMap[elementId];
        let raw = prog ? prog.get('judgement') : null;
        if (raw === 'na') {
          isNA = true;
        } else if (raw > 0) {
          judgementValue = raw;
          isPrevious = true;
          judgementSource = progressionSource(prog, store);
        }
      }

      let isEffortNA = false;
      if (sectionEntry && sectionEntry.get('effort')) {
        let rawEffort = sectionEntry.get('effort');
        if (rawEffort === 'na') {
          isEffortNA = true;
        } else {
          effortValue = parseInt(rawEffort, 10);
        }
      } else {
        let prog = effortMap[elementId];
        let raw = prog ? prog.get('effort') : null;
        if (raw === 'na') {
          isEffortNA = true;
        } else if (raw > 0) {
          effortValue = raw;
          isEffortPrevious = true;
          effortSource = progressionSource(prog, store);
        }
      }

      return {
        element,
        tooltips,
        judgementValue,
        effortValue,
        isNA,
        isEffortNA,
        isPrevious,
        isEffortPrevious,
        judgementSource,
        effortSource,
      };
    });
  }

  @action
  handleJudgementChange(elementId, value) {
    if (this.onJudgementChange) {
      this.onJudgementChange(elementId, value);
    }

    this._autoPopulateFromCriteria();
  }

  @action
  handleEffortChange(elementId, value) {
    if (this.onEffortChange) {
      this.onEffortChange(elementId, value);
    }
  }

  @action
  setNA(elementId) {
    if (this.onJudgementChange) {
      this.onJudgementChange(elementId, 'na');
    }
  }

  @action
  reEnable(elementId) {
    if (this.onJudgementChange) {
      this.onJudgementChange(elementId, null);
    }
  }

  @action
  setEffortNA(elementId) {
    if (this.onEffortChange) {
      this.onEffortChange(elementId, 'na');
    }
  }

  @action
  reEnableEffort(elementId) {
    if (this.onEffortChange) {
      this.onEffortChange(elementId, null);
    }
  }

  _autoPopulateFromCriteria() {
    if (!this.onAutoPopulate || !this.section) {
      return;
    }

    let populateData = generateRubricComments({
      store: this.store,
      student: this.student,
      reportTemplate: this.reportTemplate,
      section: this.section,
      rubricElements: this.rubricElements,
      milestone: this.currentMilestone,
    });

    if (Object.keys(populateData).length) {
      this.onAutoPopulate(populateData);
    }
  }
}
